/**
 * BH Product Card – JavaScript Controller v3.3
 * ──────────────────────────────────────────
 * Handles:
 * 1. Variant picker open/close + add-to-cart via AJAX
 * 2. Cart drawer trigger — calls BH Header's own cart drawer
 * 3. Header Height & Menu Style calculations (Moved from theme.liquid for performance)
 */

(function () {
  'use strict';

  /* ═══════════════════════════════════════════
     § 1. CART DRAWER – OPEN VIA BH HEADER
     ═══════════════════════════════════════════ */

  function openCartDrawer() {
    if (typeof window.__bhRefreshAndOpenCartDrawer === 'function') {
      window.__bhRefreshAndOpenCartDrawer();
      return;
    }
    if (typeof window.__bhOpenCartDrawer === 'function') {
      window.__bhOpenCartDrawer();
      return;
    }
    window.location.href = '/cart';
  }

  /* ═══════════════════════════════════════════
     § 2. AJAX ADD TO CART
     ═══════════════════════════════════════════ */

  async function addToCart(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Add to cart failed');
    }
    return res.json();
  }

  async function onCartAdded() {
    await new Promise((r) => setTimeout(r, 200));
    openCartDrawer();
  }

  /* ═══════════════════════════════════════════
     § 3. VARIANT PICKER & ATC — EVENT DELEGATION
     ═══════════════════════════════════════════ */

  function closeAllPickers(except) {
    document.querySelectorAll('[data-bh-variant-picker]:not([hidden])').forEach((picker) => {
      if (picker !== except) {
        picker.hidden = true;
        const btn = picker.closest('[data-bh-atc-row]')?.querySelector('[data-bh-atc-btn]');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      }
    });
  }

  async function handleAtcClick(btn) {
    if (btn.disabled || btn.hasAttribute('data-bh-loading')) return;

    const isMulti = btn.dataset.bhMultiVariant === 'true';
    const variantId = btn.dataset.variantId;
    const row = btn.closest('[data-bh-atc-row]');
    const picker = row?.querySelector('[data-bh-variant-picker]');

    if (isMulti && picker) {
      const isOpen = !picker.hidden;
      closeAllPickers();
      if (!isOpen) {
        picker.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
        const firstOpt = picker.querySelector('.bh-card__variant-opt:not(:disabled)');
        if (firstOpt) firstOpt.focus();
      }
      return;
    }

    if (!variantId) {
      console.warn('[BH Card] No variant ID on button:', btn);
      return;
    }

    btn.setAttribute('data-bh-loading', '');
    const labelEl = btn.querySelector('[data-bh-atc-label]');
    const origText = labelEl?.textContent;

    try {
      await addToCart(variantId);
      if (labelEl) labelEl.textContent = '✓ EKLENDİ';
      await onCartAdded();
      setTimeout(() => {
        if (labelEl && origText) labelEl.textContent = origText;
      }, 1500);
    } catch (err) {
      console.error('[BH Card] ATC error:', err);
      if (labelEl) labelEl.textContent = 'HATA!';
      setTimeout(() => {
        if (labelEl && origText) labelEl.textContent = origText;
      }, 2000);
    } finally {
      btn.removeAttribute('data-bh-loading');
    }
  }

  async function handleVariantClick(opt) {
    if (opt.disabled) return;

    const variantId = opt.dataset.variantId;
    if (!variantId) {
      console.warn('[BH Card] No variant ID on option:', opt);
      return;
    }

    const row = opt.closest('[data-bh-atc-row]');
    const btn = row?.querySelector('[data-bh-atc-btn]');
    const picker = row?.querySelector('[data-bh-variant-picker]');
    const labelEl = btn?.querySelector('[data-bh-atc-label]');
    const origText = labelEl?.textContent;

    opt.setAttribute('aria-selected', 'true');
    if (btn) btn.setAttribute('data-bh-loading', '');

    try {
      await addToCart(variantId);

      if (picker) picker.hidden = true;
      if (btn) btn.setAttribute('aria-expanded', 'false');

      if (labelEl) labelEl.textContent = '✓ EKLENDİ';
      await onCartAdded();
      setTimeout(() => {
        if (labelEl && origText) labelEl.textContent = origText;
      }, 1500);
    } catch (err) {
      console.error('[BH Card] Variant ATC error:', err);
      if (labelEl) labelEl.textContent = 'HATA!';
      setTimeout(() => {
        if (labelEl && origText) labelEl.textContent = origText;
      }, 2000);
    } finally {
      if (btn) btn.removeAttribute('data-bh-loading');
      opt.setAttribute('aria-selected', 'false');
    }
  }

  /* ═══════════════════════════════════════════
     § 4. SINGLE DOCUMENT-LEVEL CLICK HANDLER
     ═══════════════════════════════════════════ */

  function handleGlobalClick(e) {
    const atcBtn = e.target.closest('[data-bh-atc-btn]');
    if (atcBtn) {
      e.stopPropagation();
      e.preventDefault();
      handleAtcClick(atcBtn);
      return;
    }

    const variantOpt = e.target.closest('[data-bh-variant-opt]');
    if (variantOpt) {
      e.stopPropagation();
      e.preventDefault();
      handleVariantClick(variantOpt);
      return;
    }

    if (!e.target.closest('[data-bh-atc-row]')) {
      closeAllPickers();
    }
  }

  function handleGlobalKeydown(e) {
    if (e.key === 'Escape') {
      closeAllPickers();
      return;
    }

    const picker = e.target.closest('[data-bh-variant-picker]');
    if (!picker) return;

    const opts = Array.from(picker.querySelectorAll('.bh-card__variant-opt:not(:disabled)'));
    const idx = opts.indexOf(e.target);
    if (idx < 0) return;

    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      next = (idx + 1) % opts.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      next = (idx - 1 + opts.length) % opts.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      next = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      next = opts.length - 1;
    }

    if (next >= 0 && opts[next]) {
      opts[next].focus();
    }
  }

  /* ═══════════════════════════════════════════
     § 5. INIT & PERFORMANCE SCRIPTS
     ═══════════════════════════════════════════ */

  function init() {
    document.addEventListener('click', handleGlobalClick, true);
    document.addEventListener('keydown', handleGlobalKeydown);

    /* ★ HEADER HEIGHT & MENU STYLE LOGIC (Moved from theme.liquid) */
    (function setHeaderHeighCustomProperties() {
      const header = document.querySelector('header-component');
      const headerGroup = document.querySelector('#header-group');
      const hasHeaderSection = headerGroup?.querySelector('.header-section');
      if (!header || !headerGroup) return;

      const headerTopRow = header.querySelector('.header__row--top');
      const headerHeight = header.offsetHeight;
      let headerGroupHeight = 0;
      const children = headerGroup.children;

      for (let i = 0; i < children.length; i++) {
        const element = children[i];
        if (element === header || !(element instanceof HTMLElement)) continue;
        headerGroupHeight += element.offsetHeight;
      }

      if (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
        headerGroupHeight += headerHeight;
      }

      document.body.style.setProperty('--header-height', `${headerHeight}px`);
      document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);

      if (headerTopRow) {
        window.requestAnimationFrame(function () {
          document.body.style.setProperty('--top-row-height', `${headerTopRow.offsetHeight}px`);
        });
      }

      if (!hasHeaderSection || !header?.hasAttribute('transparent')) {
        document.body.style.setProperty('--transparent-header-offset-boolean', '0');
        return;
      }

      const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains('shopify-section');
      const shouldApplyOffset = !hasImmediateSection ? '1' : '0';
      document.body.style.setProperty('--transparent-header-offset-boolean', shouldApplyOffset);
    })();

    (function setHeaderMenuStyle() {
      const headerComponent = document.querySelector('#header-component');
      if (headerComponent) {
        const isTouchDevice = 'ontouchstart' in window && navigator.maxTouchPoints > 0;
        const overflowList = headerComponent?.querySelector('overflow-list');
        const hasReachedMinimum = overflowList && overflowList.hasAttribute('minimum-reached');
        headerComponent.dataset.menuStyle = isTouchDevice || hasReachedMinimum ? 'drawer' : 'menu';
      }
    })();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
